<template>
    <div class="related-links-widget" v-cloak>
        <div class="uploading-overlay" v-if="IsEditor && uploading">
            <div>
                <p class="text-center">Uploading {{ uploadFile.file.name }}...</p>
                <div class="progress">
                    <div class="progress-bar" role="progressbar" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100" :style="'width: ' + uploadProgress + '%;'"></div>
                </div>
                <div class="text-center">
                    <button class="btn btn-danger" @click.prevent="cancelUpload">Cancel</button>
                </div>
            </div>
        </div>

        <!-- ADD NEW LINKS AND GROUPS -->
        <div v-if="IsEditor" class="admin-control widget-admin" :class="{ choosing: newLink || newGroup }">
            <div>
                <!-- BUTTONS -->
                <button v-if="!adding" class="btn btn-sm me-1" :class="ShowConfig ? 'btn-dark' : 'btn-outline-dark'"
                        @click.prevent="ShowConfig = !ShowConfig">
                    <i class="fa-regular fa-gear"></i>
                </button>
                <button v-if="!adding && !ShowConfig" @click="createNewLink = true" class="btn btn-sm btn-primary me-1">New Link</button>
                <button v-if="!adding && !ShowConfig" @click="createNewGroup = true" class="btn btn-sm btn-primary me-1">New Group</button>
                <button v-if="!adding && dirty && !editingGroup" class="btn btn-sm btn-success me-1" @click="saveChanges">Save</button>
                <button v-if="!adding && dirty && !editingGroup" class="btn btn-sm btn-danger me-1" @click="revertChanges">Revert</button>
                <button v-if="!adding && isEmailGroupSet" class="btn btn-sm btn-outline-dark" @click="popMailModal"><i class="fa-regular fa-envelope"></i></button>
            </div>
            <div class="mt-1" :class="{ card: ShowConfig || createNewLink || newGroup }">
                <div :class="{ 'card-body': ShowConfig || createNewLink || newGroup }">
                    <!-- WIDGET CONFIG -->
                    <form v-show="ShowConfig" class="widget-config">
                        <div v-if="IsAdmin" class="form-group">
                            <label>Upload path <i class="fa-regular fa-circle-info" v-tooltip="'Combined container and subfolder path. Will only be valid if the container exists.'"></i></label>
                            <div class="input-group" :class="{ 'is-invalid': widgetV$.Config.UploadFolder.$error }">
                                <input v-model="Widget.Config.UploadFolder" type="text" class="form-control"
                                       :class="{ 'is-invalid': widgetV$.Config.UploadFolder.$error }" placeholder="container/foldername" />
                                <div v-if="uploadFolderIsValid !== null" class="input-group-text">
                                    <i v-if="uploadFolderPending" class="fa fa-spinner fa-spin"></i>
                                    <i v-if="!uploadFolderPending && !uploadFolderIsValid" class="fa fa-x text-danger"></i>
                                    <i v-if="!uploadFolderPending && uploadFolderIsValid" class="fa fa-check text-success"></i>
                                </div>
                            </div>
                            <span class="invalid-feedback" v-if="widgetV$.Config.UploadFolder.$error && widgetV$.Config.UploadFolder.$errors[0]">
                                {{ widgetV$.Config.UploadFolder.$errors[0].$message }}
                            </span>
                        </div>
                        <div v-if="IsAdmin" class="form-group">
                            <label>MyEmma groups</label>
                            <Select2 :options="emailGroupOptions" :config="{ width: '100%', multiple: true }" :value="Widget.Config.MailGroups" @input="setMailGroups($event)"></Select2>
                        </div>
                        <div class="form-group">
                            <label>No links message <i class="fa-regular fa-circle-info" v-tooltip="'Message that will be shown if there are no viewable links or groups.'"></i></label>
                            <div class="input-group">
                                <input class="form-control" type="text" v-model="Widget.Config.NoLinkText" placeholder="No message" />
                                <button class="btn btn-primary" @click.prevent="toggleNoLinkPreview">{{ noLinkPreview ? 'Reset' : 'Preview' }}</button>
                            </div>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Widget.Config.ShowFilter" /> Show Filter
                            </label>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="Widget.Config.IsCollapsible" /> Collapsible Categories
                            </label>
                        </div>
                        <div class="ms-4">
                            <div class="checkbox">
                                <label>
                                    <input type="checkbox" v-model="Widget.Config.IsAccordion"
                                           :disabled="!Widget.Config.IsCollapsible || Widget.Config.ShowCollapseAll ? 'disabled' : null" />
                                    Accordion
                                </label>
                            </div>
                            <div class="checkbox">
                                <label>
                                    <input type="checkbox" v-model="Widget.Config.ShowCollapseAll"
                                           :disabled="!Widget.Config.IsCollapsible || Widget.Config.IsAccordion ? 'disabled' : null" />
                                    Show Expand/Collapse All
                                </label>
                            </div>
                            <div class="checkbox">
                                <label>
                                    <input type="checkbox" v-model="Widget.Config.ExpandFirstGroup"
                                           :disabled="!Widget.Config.IsCollapsible ? 'disabled' : null" />
                                    Expand First Group By Default
                                </label>
                            </div>
                        </div>
                    </form>

                    <!-- ADD NEW LINK FORM -->
                    <form v-show="createNewLink">
                        <div class="form-group">
                            <label>Link Text</label>
                            <input v-model="tempText" type="text" class="form-control" :class="{ 'is-invalid': linkV$.tempText.$error }"
                                   placeholder="Go to..." />
                            <span class="invalid-feedback" v-if="linkV$.tempText.$error">Link text is required</span>
                        </div>
                        <div class="form-group">
                            <label>Link Description</label>
                            <input v-model="tempDescription" type="text" class="form-control" placeholder="Description" />
                        </div>
                        <div :class="['form-group', { 'has-error': fileExists }]">
                            <template v-if="!uploadFile">
                                <label>Link URL</label>
                                <div class="input-group">
                                    <input v-model="tempUrl" type="text" class="form-control" :class="{ 'is-invalid': linkV$.tempUrl.$error }"
                                           placeholder="http://www.example.com/somewhere or /localpath" />
                                    <button :class="['btn', 'btn-primary', { 'disabled': !Widget.Config.UploadFolder } ]" :disabled="!Widget.Config.UploadFolder" type="button" @click="openFileBrowser">Upload a file</button>
                                    <span class="invalid-feedback" v-if="linkV$.tempUrl.$error">Link URL is required</span>
                                </div>
                            </template>
                            <template v-if="uploadFile">
                                <label>File Name</label>
                                <div class="input-group">
                                    <input v-model="tempUrl" type="text" class="form-control" placeholder="example.pdf" />
                                    <button v-show="showFileNameChangeButton" :class="['btn', 'btn-success', { disabled: !fileNameValid }]" :disabled="!fileNameValid" type="button" @click="renameFile"><i class="fa-regular fa-check"></i></button>
                                    <button class="btn btn-info" type="button" @click="clearFile">Enter URL</button>
                                </div>
                            </template>
                            <span class="invalid-feedback" v-if="fileExists">
                                <a :href="uploadFileCdnUrl" target="_blank">{{uploadFile.file.name}}</a> already exists, please enter a new file name.
                                <button class="btn btn-info btn-sm" type="button" @click="setToExisting">Set to existing file's location</button>
                            </span>
                        </div>
                        <div class="checkbox">
                            <label>
                                <input type="checkbox" v-model="tempExternal" /> Open a new window on click
                            </label>
                        </div>
                        <div class="form-group">
                            <label>Start Date</label>
                            <VueDatePicker v-model="tempStart" auto-apply model-type="format"></VueDatePicker>
                        </div>
                        <div class="form-group">
                            <label class="form-label">End Date</label>
                            <VueDatePicker v-model="tempEnd" auto-apply model-type="format" :class="{ 'is-invalid': linkV$.tempEnd.$error }"></VueDatePicker>
                            <span class="invalid-feedback" v-if="linkV$.tempEnd.$error">Start date must be before end date</span>
                        </div>
                    </form>
                    <file-upload style="display: none" ref="upload" :cdn-path="CdnPath" :container-name="Widget.Config.UploadFolder"
                                 :file-chunk-size="1000 * 1048" :multiple="false" @file-selected="onFileChosen"></file-upload>
                    <file-upload style="display: none" ref="replace" :cdn-path="CdnPath" :container-name="Widget.Config.UploadFolder"
                                 :file-chunk-size="1000 * 1048" :multiple="false" @file-selected="onReplaceChosen"></file-upload>

                    <!-- ADD NEW GROUP FORM -->
                    <form v-if="newGroup">
                        <div class="form-group">
                            <label>Group Name</label>
                            <input v-model="tempGroup" type="text" class="form-control" placeholder="No group" />
                        </div>
                    </form>
                    <Select2 :options="groupOptions" :config="{ width: '100%' }" @input="selectedGroupName = $event" v-if="newLink">
                        <option value="">No group</option>
                    </Select2>
                    <div class="checkbox" v-show="adding">
                        <label>
                            <input type="checkbox" v-model="addToTop" /> Add to top
                        </label>
                    </div>
                    <div>
                        <button v-if="adding" class="btn btn-sm btn-primary me-1" @click="addItem">Add</button>
                        <button v-if="adding" class="btn btn-sm btn-danger me-1" @click="cancelAdd">Cancel</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- DISPLAYED WIDGET -->
        <div class="related-links">
            <!-- FILTER -->
            <div v-if="IsEditor || Widget.Config.ShowFilter" class="link-filter" :class="{ 'admin-control-flex': !Widget.Config.ShowFilter }">
                <label class="mb-0" :class="{ 'text-black-50': !Widget.Config.ShowFilter }">{{ translate('Filter') }}</label>
                <input class="form-control" v-model="filter" :placeholder="translate('Search')" />
            </div>
            <div v-if="Widget.Config.ShowCollapseAll" class="d-flex m-3">
                <button class="btn btn-sm btn-primary ms-auto" type="button" @click="expandOrCollapseAll">{{ translate(allCollapsed ? 'Expand All' : 'Collapse All') }}</button>
            </div>
            <!-- DISPLAY GROUPS -->
            <draggable :list="Widget.LinkGroups" group="relatedLinkWidget" handle=".drag-group" animation="250" chosen-class="drag" item-key="Id"
                       :class="{ 'admin-control': !visibleGroups && noLinkPreview }">
                <template #item="{ element: group, index }">
                    <div :class="{ 'item-hidden': group.isHidden }" v-show="!filteredGroup(group)">
                        <div class="related-link-group" :ref="el => { groupTitles[index] = el }"
                             :class="{ collapsible: Widget.Config.IsCollapsible && canCollapse(group), 'is-open': !group.collapsed }" @click="collapseGroup(index)">
                            <div v-if="IsEditor" class="h4 group-title" :class="[{ 'admin-control-flex': !group.Name }, { editing: group.editing }]">
                                <div class="title-text">
                                    <span v-if="group.Name" class="translate" v-html="highlightedGroupName(group)"></span>
                                    <em class="no-group admin-control-inline" v-if="!group.Name">Uncategorized</em>
                                </div>
                                <div class="admin-control-block group-admin">
                                    <i class="fa-regular fa-2x fa-eye-slash me-1" v-show="group.isHidden" v-tooltip="'Group is hidden because there are no visible links within it.'"></i>
                                    <i class="fa-regular fa-2x fa-pen-to-square me-1" v-show="!group.editing" role="button"
                                       @click.stop="group.editing = true"></i>
                                    <i class="fa-regular fa-2x fa-square-check text-success me-1" v-show="group.editing"
                                       role="button" @click.stop="changeGroup(group)"></i>
                                    <i class="fa-regular fa-2x fa-arrows-up-down-left-right drag-group me-1" v-if="!newLink && !groupsFiltered"
                                       @click.stop></i>
                                    <i class="fa-solid fa-2x fa-circle-minus text-danger me-1" role="button"
                                       @click.stop="removeGroup(group)"></i>
                                </div>
                            </div>
                            <div v-if="IsEditor" class="edit-group card" v-show="group.editing">
                                <div class="card-body">
                                    <div class="form-group">
                                        <label>Group Name</label>
                                        <input type="text" placeholder="group name" class="form-control" v-model="group.tempName" />
                                    </div>
                                    <div>
                                        <button class="btn btn-sm btn-success me-1" @click="changeGroup(group)">Accept</button>
                                        <button class="btn btn-sm btn-danger me-1" @click="revertGroup(group)">Cancel</button>
                                    </div>
                                </div>
                            </div>
                            <div class="h4 group-title" v-if="!IsEditor && !group.editing && group.Name"><span v-html="highlightedGroupName(group)"></span></div>
                        </div>
                        <div :ref="el => { groupLinks[index] = el }" class="collapse">
                            <draggable v-if="IsEditor" :list="group.RelatedLinks" tag="ul" group="linkGroup" handle=".drag-link" animation="250" chosen-class="drag" item-key="Id"
                                       :class="{ empty: group.RelatedLinks.length === 0 }">
                                <template #item="{ element: link }">
                                    <li :class="{ 'item-hidden': link.isHidden }" v-show="!filteredLink(link, group)">
                                        <RelatedLink v-bind="link" :IsEditor="IsEditor" :key="link.Id" :showDrag="!chooseExistingLink && !linksFiltered(group)" :CdnUrl="CdnUrl" :replacing="replacing"
                                                     :filter="debouncedFilter" @remove="removeLink(group, link)" @change-link="changeLink(link, group, $event)" @set-replace="setReplace(link)"
                                                     @confirm-replace="confirmReplace(link)" @revert-replace="revertReplace" :ref="'relatedlink-' + link.Id"></RelatedLink>
                                    </li>
                                </template>
                            </draggable>
                            <ul v-if="!IsEditor && group.RelatedLinks.length > 0">
                                <template v-for="link in group.RelatedLinks">
                                    <li v-if="!link.isHidden && !filteredLink(link, group)">
                                        <RelatedLink v-bind="link" :key="link.Id" :filter="debouncedFilter"></RelatedLink>
                                    </li>
                                </template>
                            </ul>
                        </div>
                    </div>
                </template>
            </draggable>
            <div v-show="!visibleGroups || noLinkPreview" class="h4 text-center mt-2"><em>{{ Widget.Config.NoLinkText }}</em></div>
        </div>

        <!-- EMAIL MODAL -->
        <div ref="emailModal" class="modal fade" tabindex="-1" role="dialog">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">Send email update</h4>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form>
                            <div class="form-group">
                                <label>Subject</label>
                                <input type="text" class="form-control" v-model="emailSubject" />
                            </div>
                            <div class="form-group">
                                <label>Body</label>
                                <TinyMceVue v-model="emailBody"></TinyMceVue>
                            </div>
                            <div class="checkbox">
                                <label>
                                    <input type="checkbox" v-model="sendTest" /> This is a test email
                                </label>
                            </div>
                            <div class="form-group" v-if="sendTest">
                                <label>Test Email Address</label>
                                <input type="email" class="form-control" v-model="testEmail" />
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-danger" data-bs-dismiss="modal">Close</button>
                        <button type="button" class="btn btn-primary" :class="{ 'disabled': sendTest && !testEmail }" :disabled="sendTest && !testEmail"
                                @click.prevent="sendEmail">
                            {{ 'Send' + (sendTest ? ' Test' : '') }}
                            <i v-if="sendingEmail" class="fa fa-spinner fa-spin"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, watchEffect, onMounted, nextTick, getCurrentInstance } from 'vue';
    import { useEventListener, useDebounce, useDebounceFn } from '@vueuse/core';
    import _ from 'lodash';
    import RelatedLink from './related-links-widget-link.vue';
    import Select2 from '../select2.vue';
    import FileUpload from '../port-fileupload/port-fileupload.vue';
    import draggable from 'vuedraggable';
    import dayjs from 'dayjs';
    import TinyMceVue from '../tinymce.vue';
    //import { Collapse, Modal } from 'bootstrap';
    import VueDatePicker from '@vuepic/vue-datepicker'
    import '@vuepic/vue-datepicker/dist/main.css'
    import { filterRegex } from './helpers';

    import useVuelidate from '@vuelidate/core'
    import { required, helpers } from '@vuelidate/validators'
    import { endAfterStart } from '../validators/dateValidators'

    import { useTranslations } from '../useTranslations';

    const Collapse = window.bootstrap?.Collapse;
    const Modal = window.bootstrap?.Modal;

    // Props
    const props = defineProps([
        "WidgetModel", "UpdateWidgetUrl", "AllEmailGroups", "CdnPath",
        "ContainerExistsUrl", "SendWidgetUpdateEmailUrl",
        "CurrentUrl", "IsAdmin", "IsEditor", "CreateWidgetUrl", "CdnUrl", "PurgeUrl",
        "Resources", "UserName"
    ]);

    const translate = useTranslations(props.Resources);

    // State
    const Widget = ref(_.cloneDeep(props.WidgetModel));
    const OriginalGroups = ref(_.cloneDeep(props.WidgetModel.LinkGroups));
    const OriginalConfig = ref(_.cloneDeep(props.WidgetModel.Config));    
    const ShowConfig = ref(false);
    const userName = ref(props.UserName);
    const chooseExistingLink = ref(false);
    const createNewLink = ref(false);
    const createNewGroup = ref(false);
    const tempId = ref(null);
    const tempText = ref(null);
    const tempDescription = ref(null);
    const tempUrl = ref(null);
    const tempExternal = ref(false);
    const tempStart = ref(null);
    const tempEnd = ref(null);
    const tempGroup = ref("");
    const selectedGroupName = ref("");
    const uploadFile = ref(null);
    const replaceFile = ref(null);
    const noLinkPreview = ref(false);
    const emailSubject = ref("A page on the PortOfPortland.com website has been updated");
    const emailBody = ref(`<p>Thank you for subscribing to the Port of Portland's email notification service.</p><p>A page has been updated - <a href='${props.CurrentUrl}'>click here</a> to see the change.</p>`);
    const sendTest = ref(false);
    const testEmail = ref(null);
    const addToTop = ref(false);
    const filter = ref("");
    const debouncedFilter = useDebounce(filter, 500);
    const replaceLink = ref(null);
    const replacing = ref(false);
    const groupTitles = ref([]);
    const groupLinks = ref([]);
    const emailModal = ref(null);
    const sendingEmail = ref(false);
    const uploadFolderPending = ref(false)
    const uploadFolderLastChecked = ref(null)
    const uploadFolderIsValid = ref(null)

    // Refs for file upload/replace
    const upload = ref(null);
    const replace = ref(null);

    // Vuelidate

    // new link form validations
    const linkRules = computed(() => ({
        tempText: { required },
        tempUrl: { required },
        tempEnd: {
            endAfterStart: endAfterStart(tempStart, 'Start date must be before end date')
        }
    }));

    const linkV$ = useVuelidate(linkRules, {
        tempText,
        tempUrl,
        tempEnd
    });

    // custom upload folder validator
    const uploadFolderExistsValidator = helpers.withMessage(
        'Upload path is invalid or the container does not exist',
        value => {
            if (!props.IsAdmin) return true
            if (!value?.trim()) return false

            // still waiting for a check on the current value
            if (uploadFolderPending.value) return true

            // current value has not been checked yet
            if (uploadFolderLastChecked.value !== value) return true

            return uploadFolderIsValid.value
        }
    )

    const widgetRules = computed(() => ({
        Config: {
            UploadFolder: {
                uploadFolderExists: uploadFolderExistsValidator
            }
        }
    }))

    const widgetV$ = useVuelidate(widgetRules, Widget)

    // set up model before creating
    Widget.value.LinkGroups.forEach((g, i) => {
        g.editing = false;
        g.tempName = g.Name;
        g.collapsed = Widget.value.Config.IsCollapsible && !(i === 0 && Widget.value.Config.ExpandFirstGroup) && canCollapse(g);
        g.RelatedLinks.forEach(rl => {
            rl.isHidden = isHiddenLink(rl);
        });
        // set hidden links first because group visibility is dependent on them
        g.isHidden = isHiddenGroup(g);
    });

    // Computed
    const newLink = computed({
        get: () => chooseExistingLink.value || createNewLink.value,
        set: (val) => {
            if (!val) {
                chooseExistingLink.value = false;
                createNewLink.value = false;
                tempId.value = null;
                tempText.value = null;
                tempDescription.value = null;
                tempUrl.value = null;
                tempExternal.value = false;
                tempStart.value = null;
                tempEnd.value = null;
                selectedGroupName.value = "";
                clearFile();
            }
        }
    });

    const newGroup = computed({
        get: () => createNewGroup.value,
        set: (val) => {
            if (!val) {
                createNewGroup.value = false;
                tempGroup.value = "";
            }
        }
    });

    const selectedGroup = computed({
        get: () => {
            const foundGroup = _.find(Widget.value.LinkGroups, g => {
                if (!g.Name && !selectedGroupName.value) return true;
                return g.Name === selectedGroupName.value;
            });
            return foundGroup ? foundGroup : addGroup();
        },
        set: (val) => {
            selectedGroupName.value = val ? val.Name : "";
        }
    });

    const adding = computed(() => newLink.value || newGroup.value);

    const dirty = computed(() => {
        let configChanged = false;
        for (const key in Widget.value.Config) {
            if (key !== "Properties" && !_.isEqual(OriginalConfig.value[key], Widget.value.Config[key])
                && (OriginalConfig.value[key] || Widget.value.Config[key])) {
                configChanged = true;
                break;
            }
        }
        if (configChanged) return true;
        if (Widget.value.LinkGroups.length !== OriginalGroups.value.length) return true;
        for (let i = 0; i < Widget.value.LinkGroups.length; i++) {
            const thisGroup = Widget.value.LinkGroups[i];
            const thisOriginalGroup = OriginalGroups.value[i];
            if (thisGroup.Id !== thisOriginalGroup.Id || thisGroup.Name !== thisOriginalGroup.Name) return true;
            if (thisGroup.RelatedLinks.length !== thisOriginalGroup.RelatedLinks.length) return true;
            for (let j = 0; j < thisGroup.RelatedLinks.length; j++) {
                const thisLink = thisGroup.RelatedLinks[j];
                const thisOriginalLink = thisOriginalGroup.RelatedLinks[j];
                if (thisLink.Id !== thisOriginalLink.Id ||
                    thisLink.Text !== thisOriginalLink.Text ||
                    thisLink.Description !== thisOriginalLink.Description ||
                    thisLink.Url !== thisOriginalLink.Url ||
                    thisLink.IsExternal !== thisOriginalLink.IsExternal ||
                    thisLink.Start !== thisOriginalLink.Start ||
                    thisLink.End !== thisOriginalLink.End) {
                    return true;
                }
            }
        }
        return false;
    });

    const groupOptions = computed(() =>
        $.map(Widget.value.LinkGroups, group => ({ id: group.Name, text: group.Name }))
    );

    const emailGroupOptions = computed(() =>
        $.map(props.AllEmailGroups, group => ({ id: group.Id, text: group.Name }))
    );

    const editingGroup = computed(() =>
        Widget.value.LinkGroups.reduce((total, current) => total || current.editing, false)
    );

    const fileUploadBus = computed(() => upload.value ? upload.value.eventBus() : null);

    const fileReplaceBus = computed(() => replace.value ? replace.value.eventBus() : null);

    const fileExists = computed(() =>
        uploadFile.value && uploadFile.value.fileUi && uploadFile.value.fileUi.exists
    );

    const showFileNameChangeButton = computed(() =>
        fileExists.value || (uploadFile.value && uploadFile.value.file.name !== tempUrl.value)
    );

    const fileNameValid = computed(() =>
        tempUrl.value && (!fileExists.value || uploadFile.value.file.name !== tempUrl.value)
    );

    const uploadFileCdnUrl = computed(() =>
        props.CdnPath + (upload.value?.containerName ?? '') + "/" + (uploadFile.value?.file.name ?? '')
    );

    const uploading = computed(() =>
        !!uploadFile.value && upload.value?.isUploading
    );

    const uploadProgress = computed(() =>
        uploadFile.value ? uploadFile.value.fileUi.progress : 0
    );

    const visibleGroups = computed(() =>
        Widget.value.LinkGroups.some(g => !g.isHidden)
    );

    const isEmailGroupSet = computed(() =>
        Widget.value.Config.MailGroups && Widget.value.Config.MailGroups.length > 0
    );

    const instance = getCurrentInstance();

    const replaceLinkComponent = computed(() => {
        if (!replaceLink.value) return null;
        const ref = instance?.proxy?.$refs['relatedlink-' + replaceLink.value.Id];
        // If ref is an array, use the first element
        return Array.isArray(ref) ? ref[0] : ref;
    });

    const groupsFiltered = computed(() =>
        Widget.value.LinkGroups.some(g => filteredGroup(g))
    );

    const allCollapsed = computed(() =>
        _(Widget.value.LinkGroups)
            .filter(lg => canCollapse(lg))
            .every(lg => lg.collapsed)
    );

    const highlightRegex = computed(() => filterRegex(debouncedFilter.value))

    const normalizedFilter = computed(() => debouncedFilter.value?.trim().toLowerCase() || '')

    // Methods
    function cloneObject(obj) {
        return _.cloneDeep(obj);
    }

    async function addLink() {

        const isValid = await linkV$.value.$validate();
        if (!isValid) return;

        if (!tempText.value || !tempUrl.value) return;

        if (uploadFile.value) {
            fileUploadBus.value?.emit("startUpload");
            return;
        }

        addLinkToSelectedGroup(createRelatedLink(tempUrl.value));
    }

    function addLinkToSelectedGroup(link) {
        addToTop.value ? selectedGroup.value.RelatedLinks.unshift(link) : selectedGroup.value.RelatedLinks.push(link);
        newLink.value = false;
    }

    function removeLink(group, link) {
        group.RelatedLinks.splice(group.RelatedLinks.indexOf(link), 1);
    }

    function removeGroup(group) {
        if (group.RelatedLinks.length > 0) {
            if (confirm("There are still links within this group. Are you sure you want to delete it?")) {
                Widget.value.LinkGroups.splice(Widget.value.LinkGroups.indexOf(group), 1);
            }
        } else {
            Widget.value.LinkGroups.splice(Widget.value.LinkGroups.indexOf(group), 1);
        }
    }

    function addGroup() {
        const newGroupObj = {
            Id: 0,
            Name: tempGroup.value,
            RelatedLinks: [],
            editing: false,
            isHidden: true,
            collapsed: true
        };
        addToTop.value ? Widget.value.LinkGroups.unshift(newGroupObj) : Widget.value.LinkGroups.push(newGroupObj);
        newGroup.value = false;
        return newGroupObj;
    }

    function addItem() {
        if (chooseExistingLink.value || createNewLink.value) {
            addLink();
        } else if (createNewGroup.value) {
            addGroup();
        }
    }

    function cancelAdd() {
        linkV$.value.$reset();
        newLink.value = false;
        newGroup.value = false;
    }

    async function saveChanges() {
        const isValid = await widgetV$.value.$validate()
        if (!isValid) {
            ShowConfig.value = true
            return
        }

        Messenger().run(
            {
                action: $.ajax,
                successMessage: "Changes have been saved",
                errorMessage: "There was an error saving",
                progressMessage: "Saving..."
            },
            {
                method: "PUT",
                url: props.UpdateWidgetUrl,
                contentType: 'application/json',
                data: JSON.stringify({
                    WidgetId: Widget.value.WidgetId,
                    WidgetTypeId: Widget.value.WidgetTypeId,
                    PageId: Widget.value.PageId,
                    SectionId: Widget.value.SectionId,
                    LinkGroups: Widget.value.LinkGroups,
                    Config: Widget.value.Config
                }),
                success: function (data) {
                    if (data.RelatedLinks) {
                        Widget.value.LinkGroups = data.LinkGroups
                    }

                    OriginalGroups.value = cloneObject(Widget.value.LinkGroups)
                    OriginalConfig.value = cloneObject(Widget.value.Config)
                    widgetV$.value.$reset()
                }
            }
        )
    }

    function revertChanges() {
        Widget.value.Config = cloneObject(OriginalConfig.value);
        Widget.value.LinkGroups = cloneObject(OriginalGroups.value);

        widgetV$.value.$reset();
    }

    function clearFile() {
        uploadFile.value = null;
        if (Widget.value.Config.UploadFolder) {
            fileUploadBus.value?.emit("removeAllFiles");
        }
    }

    function openFileBrowser() {
        if (Widget.value.Config.UploadFolder) {
            fileUploadBus.value?.emit("openFileBrowser");
        }
    }

    function onFileChosen(fileInfo) {
        uploadFile.value = fileInfo;
        fileUploadBus.value?.emit("removeAllFiles", fileInfo);
    }

    function onReplaceChosen(fileInfo) {
        replaceFile.value = fileInfo;
        fileReplaceBus.value?.emit("removeAllFiles", fileInfo);
    }

    function renameFile() {
        fileUploadBus.value?.emit("fileRename", uploadFile.value.file.name, tempUrl.value);
    }

    function setToExisting() {
        const cdnUrl = uploadFileCdnUrl.value;
        uploadFile.value = null;
        nextTick(() => {
            tempUrl.value = cdnUrl;
        });
    }

    function changeGroup(group) {
        group.Name = group.tempName;
        group.editing = false;
    }

    function revertGroup(group) {
        group.tempName = group.Name;
        group.editing = false;
    }

    function changeLink(link, group, newVals) {
        link.Text = newVals.text;
        link.Description = newVals.description;
        link.Url = newVals.url;
        link.IsExternal = newVals.isExternal;
        link.Start = newVals.start;
        link.End = newVals.end;
        link.UpdatedBy = userName.value;
        link.isHidden = isHiddenLink(link);
        group.isHidden = isHiddenGroup(group);
    }

    const debouncedUploadFolder = useDebounce(
        computed(() => Widget.value.Config.UploadFolder),
        400
    )

    async function validateUploadFolderNow(path) {
        if (!path?.trim()) return false

        try {
            const exists = await $.ajax({
                type: 'POST',
                url: props.ContainerExistsUrl,
                data: { path }
            })

            return !!exists
        } catch {
            return false
        }
    }

    function createRelatedLink(url) {
        const link = {
            Id: 0,
            Text: tempText.value,
            Description: tempDescription.value,
            Url: url,
            IsExternal: tempExternal.value,
            Start: tempStart.value,
            End: tempEnd.value,
            CreatedBy: userName.value
        };

        link.isHidden = isHiddenLink(link);

        return link;
    }

    function isHiddenLink(link) {
        return !!(link.Start && dayjs(link.Start).isAfter()) ||
            !!(link.End && dayjs(link.End).isBefore());
    }

    function isHiddenGroup(group) {
        return !group.RelatedLinks.some(l => !l.isHidden);
    }

    function toggleNoLinkPreview() {
        noLinkPreview.value = !noLinkPreview.value;
    }

    function setMailGroups(ids) {
        Widget.value.Config.MailGroups = ids ? $.map(ids, id => parseInt(id)) : [];
        if (!ids) {
            Widget.value.Config.Properties.MailGroups = "";
        }
    }

    function cancelUpload() {
        fileUploadBus.value?.emit("cancelUpload");
    }

    function filteredGroup(group) {
        const f = normalizedFilter.value
        if (!f) return false

        return (!group.Name || !group.Name.toLowerCase().includes(f)) &&
            !_.some(group.RelatedLinks, l => !filteredLink(l))
    }

    function filteredLink(link, group) {
        const f = normalizedFilter.value
        if (!f) return false

        return !link.Text.toLowerCase().includes(f) &&
            (group && group.Name ? !group.Name.toLowerCase().includes(f) : true)
    }

    function linksFiltered(group) {
        return _.some(group.RelatedLinks, l => filteredLink(l));
    }

    function setReplace(link) {
        if (replaceLink.value) {
            replaceLinkComponent.value?.setReplacePreviewUrl(null);
            replacing.value = false;
        }
        replaceLink.value = link;
        if (link && Widget.value.Config.UploadFolder) {
            fileReplaceBus.value?.emit("openFileBrowser");
        }
    }

    function confirmReplace() {
        let message = "Are you sure you want to replace this file? It cannot be undone.";
        if (window.confirm(message)) {
            replacing.value = true;
            fileReplaceBus.value?.emit("startUpload");
        }
    }

    function revertReplace() {
        URL.revokeObjectURL(replaceFile.value.file.rawFile);
        setReplace(null);
        replaceFile.value = null;
    }

    function highlightedGroupName(group) {
        if (!highlightRegex.value) return group.Name
        return group.Name.replace(highlightRegex.value, '<span class="highlight">$1</span>')
    }

    function collapseGroup(index) {
        if (!Widget.value.Config.IsCollapsible) return;

        if (Widget.value.Config.IsAccordion) {
            Widget.value.LinkGroups.forEach((lg, i) => {
                if (i !== index && canCollapse(lg)) {
                    lg.collapsed = true;
                }
            });
        }
        const group = Widget.value.LinkGroups[index];
        if (canCollapse(group)) {
            group.collapsed = !group.collapsed;
        }
    }

    function expandOrCollapseAll() {
        const expand = allCollapsed.value;
        Widget.value.LinkGroups.forEach(group => {
            if (canCollapse(group)) {
                group.collapsed = !expand;
            }
        });
    }

    function canCollapse(group) {
        return !!group.Name && group.RelatedLinks.length > 0;
    }

    function popMailModal() {
        let modal = Modal.getOrCreateInstance(emailModal.value);
        modal.show();
    }

    function sendEmail() {
        sendingEmail.value = true;
        Messenger().run(
            {
                action: $.ajax,
                successMessage: (sendTest.value ? "Test e" : "E") + "mail sent",
                errorMessage: "There was an error sending email",
                progressMessage: "Sending email..."
            },
            {
                method: "POST",
                url: props.SendWidgetUpdateEmailUrl,
                data: {
                    Subject: emailSubject.value,
                    Body: emailBody.value,
                    MyEmmaGroups: Widget.value.Config.MailGroups,
                    TestEmail: sendTest.value ? testEmail.value : null
                },
                success: function () {
                    sendingEmail.value = false;
                    const modal = Modal.getOrCreateInstance(emailModal.value);
                    modal.hide();
                }
            }
        );
    }

    // Lifecycle
    onMounted(() => {
        $(window).trigger("widget-loaded");

        useEventListener(window, "beforeunload", (e) => {
            if (dirty.value) {
                e.preventDefault();
                e.returnValue = true;
            }
        });
        OriginalConfig.value = cloneObject(Widget.value.Config);
        OriginalGroups.value = cloneObject(Widget.value.LinkGroups);
        emailBody.value = `<p>Thank you for subscribing to the Port of Portland's email notification service.</p><p>A page has been updated - <a href='${props.CurrentUrl}'>click here</a> to see the change.</p>`;
    });

    // Watchers
    watch(uploadFile, (newVal) => {
        if (newVal) {
            tempUrl.value = newVal.file.name;
        } else {
            tempUrl.value = null;
            fileUploadBus.value?.emit("removeAllFiles");
        }
    });

    watch(replaceFile, (newVal) => {
        if (newVal) {
            const slashIndex = replaceLink.value.Url.lastIndexOf('/') + 1;
            const newName = replaceLink.value.Url.substring(slashIndex);
            fileReplaceBus.value?.emit("fileRename", { oldName: newVal.file.name, newName });
            const previewUrl = URL.createObjectURL(newVal.file.rawFile);
            replaceLinkComponent.value?.setReplacePreviewUrl(previewUrl);
        } else {
            fileReplaceBus.value?.emit("removeAllFiles");
        }
    });

watch(() => uploadFile.value?.fileUi?.uploaded, (uploaded) => {
        if (!uploaded) return;
            
        addLinkToSelectedGroup(createRelatedLink(uploadFile.value.fileUi.link));
        
    });

    watch(() => replaceFile.value?.fileUi?.uploaded, (newVal) => {
        if (newVal) {
            const linkComponent = replaceLinkComponent.value;
            linkComponent?.setAlertMessage("Initiating purge request... <i class='fa-regular fa-spin fa-spinner'></i>", "warning");
            $.ajax({
                url: props.PurgeUrl,
                method: "POST",
                data: {
                    path: "/" + Widget.value.Config.UploadFolder + "/" + replaceFile.value.file.name
                },
                success: function () {
                    linkComponent?.setAlertMessage("Cache purge initiated - it may take up to 45 minutes for replaced files to update.", "warning");
                },
                error: function () {
                    linkComponent?.setAlertMessage("Error: unable to initiate purge request.", "danger");
                }
            });
            revertReplace();
        }
    });

    watch(() => Widget.value.LinkGroups, (newVal) => {
        newVal.forEach(g => {
            g.isHidden = isHiddenGroup(g);
        });
    }, { deep: true });

    watch(() => Widget.value.Config.IsCollapsible, (newVal) => {
        if (!newVal) {
            Widget.value.Config.IsAccordion = false;
            Widget.value.Config.ShowCollapseAll = false;
            Widget.value.Config.ExpandFirstGroup = false;
        }
    });

    // Sync Bootstrap collapse with group.collapsed
    watchEffect(() => {

        Widget.value.LinkGroups.forEach((group, i) => {
            const el = groupLinks.value[i];
            if (!el) return;

            const bsCollapse = Collapse.getOrCreateInstance(el, {
                toggle: false
            });

            if (Widget.value.Config.IsCollapsible && group.collapsed) {
                bsCollapse.hide();
            } else {
                bsCollapse.show();
            }
        });
    });

    watch(debouncedFilter, (newVal) => {
        const f = newVal.trim();
        if (f) {
            _(Widget.value.LinkGroups)
                .filter(lg => lg.collapsed)
                .forEach(lg => {
                    if (_.some(lg.RelatedLinks, rl => !rl.isHidden)) {
                        setTimeout(() => { lg.collapsed = false; });
                    }
                });
        }
    });

    const runUploadFolderCheck = useDebounceFn(async (path) => {
        uploadFolderPending.value = true

        const result = await validateUploadFolderNow(path)

        uploadFolderLastChecked.value = path
        uploadFolderIsValid.value = result
        uploadFolderPending.value = false

        widgetV$.value.Config.UploadFolder.$validate()
    }, 400)

    watch(
        () => Widget.value.Config.UploadFolder,
        (path) => {
            uploadFolderPending.value = !!path?.trim()
            uploadFolderLastChecked.value = null
            uploadFolderIsValid.value = false

            if (!props.IsAdmin) return

            if (!path?.trim()) {
                uploadFolderPending.value = false
                return
            }

            runUploadFolderCheck(path)
            widgetV$.value.Config.UploadFolder.$validate()
        }
    )
</script>